import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { CloseIcon, CheckIcon } from '@picsio/icons';
import { Input, IconButton } from '@picsio/ui';
import s from './InputEdit.module.scss';

const BLUR_ACTION_TIMEOUT_MS = 200;

const InputEdit = (props) => {
  const {
    className, name, onCancel, onSave, validation, defaultValue,
  } = props;
  const isButtonPressed = useRef(false);
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState(false);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleChange = ({ target: { value: targetValue } }) => {
    const isValid = validation ? validation(targetValue) : true;

    setValue(targetValue);

    if (!targetValue || !isValid) {
      setError(true);
    } else if (error) {
      setError(false);
    }
  };

  const handleFocus = ({ target }) => {
    target.select();
  };

  const save = () => {
    if (!error && value !== defaultValue) {
      onSave(value);
    }
    onCancel();
  };

  const handleBlur = () => {
    setTimeout(() => {
      /** if button 'save' or 'cancel' IS NOT pressed -> save */
      if (!isButtonPressed.current) save();
      isButtonPressed.current = false;
    }, BLUR_ACTION_TIMEOUT_MS);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    isButtonPressed.current = true;
    onCancel();
  };

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isButtonPressed.current = true;
    save();
  };

  const handleKeyDown = (e) => {
    const { key } = e;
    switch (key) {
    case 'Escape':
      handleCancel(e);
      break;
    case 'Enter':
      handleSave(e);
      break;
    default:
      break;
    }
  };

  return (
    <div className={cn(s.container, className)}>
      <div className={s.input}>
        <Input
          autoFocus
          error={error}
          name={name}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          value={value}
        />
      </div>
      <div className={s.actions}>
        <IconButton
          onClick={handleCancel}
          size="md"
          componentProps={{
            'data-testid': 'btnEditCancel',
          }}
        >
          <CloseIcon />
        </IconButton>
        <IconButton
          onClick={handleSave}
          size="md"
          componentProps={{
            'data-testid': 'btnEditSave',
          }}
        >
          <CheckIcon />
        </IconButton>
      </div>
    </div>
  );
};

InputEdit.defaultProps = {
  className: '',
  validation: null,
  defaultValue: '',
};
InputEdit.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  validation: PropTypes.func,
};

export default InputEdit;
